import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControl, useMediaQuery, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledInputLabel, StyledMenuItem, StyledSelect } from 'common/theme/fields/StyledSelect';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';

function SelectFieldAdapter({ input, id, label, options, onChange: customOnChange = null, ...rest }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const size = useMemo(() => (isDesktop ? 'medium' : 'small'), [isDesktop]);

  const onChange = (event) => {
    const { value } = event.target;
    input.onChange(value);

    // Si hay un onChange custom, llamarlo también
    if (customOnChange) {
      customOnChange(event, value);
    }
  };

  // Función para obtener el icono apropiado según el label de la opción
  const getOptionIcon = (optionLabel) => {
    if (!optionLabel) return null;
    const labelLower = optionLabel.toLowerCase();

    // Para opciones de estado completo/incompleto
    if (labelLower.includes('todas') || labelLower.includes('todos')) {
      return <CheckCircleIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }
    if (labelLower.includes('completas') || labelLower.includes('completos')) {
      return <CheckCircleIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }
    if (labelLower.includes('pendiente') || labelLower.includes('incompleta')) {
      return <CancelIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }

    // Para opciones de eliminado
    if (labelLower.includes('eliminado')) {
      return <DeleteIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }
    if (labelLower.includes('restaurado') || labelLower.includes('activo')) {
      return <RestoreIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }

    // Para opciones de cliente
    if (labelLower.includes('cliente')) {
      return <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />;
    }

    return null;
  };

  return (
    <FormControl size={size} {...rest}>
      <StyledInputLabel id={id}>{label}</StyledInputLabel>
      <StyledSelect
        {...rest}
        label={label}
        labelId={id}
        id={id}
        value={input.value}
        onChange={onChange}
        MenuProps={{
          // El estilo del popover (superficie/hover/selected) viene del override global
          // MuiMenu/MuiMenuItem en theme/components.js. Aquí sólo límites y anclaje.
          PaperProps: {
            sx: {
              maxHeight: '400px !important',
              maxWidth: '500px !important',
              width: 'auto !important'
            }
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
        sx={{
          // En claro el borde debe ser NEGRO marcado (antes era text.disabled = casi
          // invisible sobre la card blanca). Icono/hover/foco en dorado del modo (gold).
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor:
              theme.palette.mode === 'light' ? 'rgba(17, 21, 27, 0.6)' : theme.palette.text.disabled
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.main
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.secondary.main
          }
        }}
      >
        {options.filter(Boolean).map((option) => {
          const icon = getOptionIcon(option.label);
          return (
            <StyledMenuItem key={option.value} value={option.value}>
              {icon ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  {icon}
                  <Typography>{option.label}</Typography>
                </Stack>
              ) : (
                option.label
              )}
            </StyledMenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
}

SelectFieldAdapter.propTypes = {
  input: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func
};

export default SelectFieldAdapter;
